/*
 * Investment Dropdown
 * */

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import { loadState } from 'app-components/hooks/utils';

import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

import LoadingView from 'components/LoadingView';
import ZeroStateView from 'components/ZeroStateView';

import useStyles from './styles';

const BootstrapInput = withStyles(InputBase, (theme) => ({
  input: {
    padding: '8px 0px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.main}`,
    display: 'flex',
    flexDirection: 'row',
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.mainr,
    },
    '&:focus': {
      marginLeft: -1,
      border: `2px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
    },
  },
}));

const DisabledBootstrapInput = withStyles(InputBase, (theme) => ({
  input: {
    padding: '8px 0px',
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    display: 'flex',
    color: theme.palette.text.disabled,
    flexDirection: 'row',
  },
}));

const SimplifiStyledSelect = (props) => {
  const {
    onOpen,
    MenuProps,
    id,
    open,
    className,
    overrideClass,
    multiple,
    value,
    onClose,
    onChange,
    IconComponent,
    displayEmpty,
    renderValue,
    children,
    viewState,
    disabled,
    showZeroState,
    sharedcomponentid,
    anchorEl,
    customPopupContent,
    customPopoverProps,
    ...other
  } = props;
  const { classes } = useStyles();

  const formatRenderValue = useCallback(() => {
    const renderItem = renderValue(value);
    if (typeof renderItem === 'string') {
      return (
        <div className={classes.textContainer}>
          <Typography className={classes.textBox}>{renderItem}</Typography>
        </div>
      );
    }
    return renderItem;
  }, [value, classes, renderValue]);

  const emptyView = useMemo(
    () => (
      <ZeroStateView
        size='small'
        id='simplifi-select-zero-state'
        title='No data'
        description='You currently have no data to display'
        qIllustration='emptyBox'
      />
    ),
    [],
  );

  const failedToFetch = useMemo(
    () => (
      <ZeroStateView
        size='small'
        id='simplifi-select-failed-to-fetch'
        title='Failed to fetch data'
        description='Please refresh the app to try again'
        qIllustration='planeCrash'
      />
    ),
    [],
  );

  const loadingView = useMemo(() => <LoadingView size={50} className={classes.zeroStyles} />, [classes]);

  const pickStateToDisplay = useMemo(() => {
    if (loadState.noDataStates.includes(viewState) || showZeroState) {
      return emptyView;
    }

    if (loadState.tryAgainStates.includes(viewState) || loadState.updateRequiredStates.includes(viewState)) {
      return failedToFetch;
    }

    if (loadState.skeletonStates.includes(viewState)) {
      return loadingView;
    }

    return children;
  }, [children, emptyView, failedToFetch, loadingView, showZeroState, viewState]);

  const displayValue = typeof renderValue === 'function' ? formatRenderValue() : renderValue;
  const SelectIcon = IconComponent || ExpandMoreIcon;
  const useCustomPopup = Boolean(customPopupContent);

  return (
    <FormControl
      sharedcomponentid={sharedcomponentid}
      variant='filled'
      className={!overrideClass ? classNames(classes.formControl, className) : className}
    >
      {useCustomPopup ? (
        <>
          <Box
            id={id}
            role='button'
            tabIndex={disabled ? -1 : 0}
            aria-expanded={open}
            aria-haspopup='dialog'
            className={classNames(
              classes.formBox,
              classes.customTriggerRoot,
              disabled ? classes.customTriggerDisabled : '',
            )}
            onClick={
              disabled
                ? undefined
                : (event) => {
                    onOpen?.(event);
                  }
            }
            onKeyDown={
              disabled
                ? undefined
                : (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpen?.(event);
                    }
                  }
            }
          >
            <Box className={classes.customTriggerContent}>
              <Box className={classes.customTriggerValue}>{displayValue}</Box>
              <Box className={classes.customTriggerIcon}>
                <SelectIcon />
              </Box>
            </Box>
          </Box>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            {...customPopoverProps}
          >
            {pickStateToDisplay === children ? customPopupContent : pickStateToDisplay}
          </Popover>
        </>
      ) : (
        <Select
          disabled={disabled}
          sharedcomponentid='SIMPLIFI_STYLED_SELECT'
          onOpen={onOpen}
          MenuProps={MenuProps}
          id={id}
          open={open}
          input={disabled ? <DisabledBootstrapInput id={`${id}-input`} /> : <BootstrapInput id={`${id}-input`} />}
          className={classes.formBox}
          multiple={multiple}
          value={value}
          onClose={onClose}
          onChange={onChange}
          IconComponent={SelectIcon}
          displayEmpty={displayEmpty}
          renderValue={typeof renderValue === 'function' ? formatRenderValue : renderValue}
          {...other}
        >
          {pickStateToDisplay}
        </Select>
      )}
    </FormControl>
  );
};

SimplifiStyledSelect.propTypes = {
  MenuProps: PropTypes.object,
  IconComponent: PropTypes.any,
  anchorEl: PropTypes.any,
  children: PropTypes.any,
  className: PropTypes.string,
  customPopupContent: PropTypes.node,
  customPopoverProps: PropTypes.object,
  disabled: PropTypes.bool,
  displayEmpty: PropTypes.any,
  id: PropTypes.string,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  overrideClass: PropTypes.bool,
  renderValue: PropTypes.any,
  sharedcomponentid: PropTypes.string,
  showZeroState: PropTypes.bool,
  value: PropTypes.any,
  viewState: PropTypes.string,
};

export default SimplifiStyledSelect;
